/**
 * ShareToClientModal Component
 * Feature: Matchmaking - Share Provider to Client
 *
 * Modal for sharing a service provider to a specific client
 * Opens when clicking the share button on a matchmaking result card
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useClients } from '../hooks/useMatchmaking';
import { useCurrentUserProfile } from '../hooks/useMatchmaking';
import { useProviderReferrals } from '@/features/dashboard/financial-advisor/hooks/useProviderReferrals';
import { useReferProvider } from '@/features/dashboard/financial-advisor/hooks/useReferProvider';

type ShareToClientModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string | null;
  providerName: string;
  clientId: string | null;
};

export function ShareToClientModal({
  open,
  onOpenChange,
  providerId,
  providerName,
  clientId,
}: ShareToClientModalProps) {
  // Feature 036: Get current user profile to pass role to useClients
  const { data: currentUser } = useCurrentUserProfile();
  const { data: clientsResponse, isLoading: isLoadingClients } = useClients(currentUser?.role);
  const clients = clientsResponse?.clients || [];
  const { data: existingReferrals = [], isLoading: isLoadingReferrals } =
    useProviderReferrals(providerId);
  const { mutate: referProvider, isPending } = useReferProvider();
  // Feature 102: required attestation + client-name opt-out (default on).
  const [attested, setAttested] = useState(false);
  const [shareClientName, setShareClientName] = useState(true);

  useEffect(() => {
    if (open) {
      setAttested(false);
      setShareClientName(true);
    }
  }, [open, providerId, clientId]);

  // Find the specific client
  const client = useMemo(() => {
    if (!clientId) return null;
    return clients.find((c) => c.id === clientId) || null;
  }, [clients, clientId]);

  // Check if this client has already been referred
  const isAlreadyReferred = useMemo(() => {
    if (!clientId || !existingReferrals.length) return false;
    return existingReferrals.some((ref) => ref.clientId === clientId);
  }, [existingReferrals, clientId]);

  // Handle submit
  const handleSubmit = () => {
    if (!providerId || !clientId || !attested) {
      console.error('Cannot submit referral: missing providerId, clientId, or attestation.', {
        providerId,
        clientId,
        attested,
      });
      return;
    }

    referProvider(
      {
        providerId,
        clientIds: [clientId],
        attested,
        shareClientName,
      },
      {
        onSuccess: () => {
          setAttested(false);
          setShareClientName(true);
          onOpenChange(false);
        },
        onError: (error) => {
          console.error('Failed to send referral:', error);
        },
      },
    );
  };

  // Handle close
  const handleClose = () => {
    if (!isPending) {
      setAttested(false);
      setShareClientName(true);
      onOpenChange(false);
    }
  };

  const isLoading = isLoadingClients || isLoadingReferrals;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Share {providerName} to Client</DialogTitle>
          <DialogDescription>
            Send this provider's details to your client. They'll receive an email
            with the provider's information.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : !client ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-gray-900 mb-2">Client not found</h4>
            <p className="text-sm text-gray-500 mb-6 max-w-sm">
              Unable to find the selected client. Please try again.
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <>
            {/* Client Info Card */}
            <div className="space-y-4">
              <div
                className={`flex items-center gap-4 p-4 border rounded-lg ${
                  isAlreadyReferred
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                {client.profilePhoto ? (
                  <img
                    src={client.profilePhoto}
                    alt={client.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#026857]/10 flex items-center justify-center">
                    <span className="text-[#026857] font-semibold text-lg">
                      {client.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </span>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-base font-medium text-gray-900 truncate">
                      {client.name}
                    </h4>
                    {isAlreadyReferred && (
                      <span className="flex items-center gap-1 text-xs text-[#026857] bg-[#026857]/10 px-2 py-0.5 rounded-full shrink-0">
                        <CheckCircle2 className="w-3 h-3" />
                        Already referred
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{client.email}</p>
                </div>
              </div>

              {isAlreadyReferred && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    This provider has already been referred to {client.name}. The
                    referral cannot be sent again.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Feature 102: consent controls */}
            <div className="border-t pt-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attested}
                  disabled={isPending}
                  onChange={() => setAttested((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  I understand my name and profile link will be shared with{' '}
                  {providerName || 'this provider'} so they know who referred them.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareClientName}
                  disabled={isPending}
                  onChange={() => setShareClientName((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  Share my client's first name and last initial with{' '}
                  {providerName || 'this provider'} (never their contact info)
                </span>
              </label>
            </div>

            {/* Footer */}
            <DialogFooter className="border-t pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isPending || isAlreadyReferred || !attested}
                className="bg-[#026857] hover:bg-[#025247] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? 'Sending...' : 'Send Referral'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

